import { License } from '../../Participants/AddParticipant/Stores/Types';
import { T } from '../../../../Translations';
import { aggregateAvailability } from '../../LicenseAvailability';
import AvailabilityBar from '../../AvailabilityBar';
import LicenseTermRow from './LicenseTermRow';

interface Props {
  typeName: string;
  licenses: ReadonlyArray<License>;
  selectedLicenseIds: number[];
  onSelectedLicenseIdsChange: (ids: number[]) => void;
  expanded: boolean;
  onToggleExpanded: (typeName: string) => void;
}

/**
 * One license-type card: a select-all checkbox, the type name and term count,
 * the availability summed across the type's terms, and an expandable list of
 * the individual terms.
 */
function LicenseTypeCard({
  typeName,
  licenses,
  selectedLicenseIds,
  onSelectedLicenseIdsChange,
  expanded,
  onToggleExpanded,
}: Props) {
  const allSelected = licenses.every((l) => selectedLicenseIds.includes(l.id));
  const someSelected = licenses.some((l) => selectedLicenseIds.includes(l.id));
  const availability = aggregateAvailability(licenses.map((l) => l.quantityAvailable));

  const handleToggleAll = (checked: boolean) => {
    const licenseIds = licenses.map((l) => l.id);
    if (checked) {
      onSelectedLicenseIdsChange([...new Set([...selectedLicenseIds, ...licenseIds])]);
    } else {
      const removed = new Set(licenseIds);
      onSelectedLicenseIdsChange(selectedLicenseIds.filter((id) => !removed.has(id)));
    }
  };

  const handleToggleLicense = (licenseId: number, checked: boolean) => {
    if (checked) {
      onSelectedLicenseIdsChange([...selectedLicenseIds, licenseId]);
    } else {
      onSelectedLicenseIdsChange(selectedLicenseIds.filter((id) => id !== licenseId));
    }
  };

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <div className="p-4 bg-card">
        <div className="flex items-start gap-3 mb-3">
          <input
            type="checkbox"
            checked={allSelected}
            ref={(el) => {
              if (el) el.indeterminate = someSelected && !allSelected;
            }}
            onChange={(e) => handleToggleAll(e.target.checked)}
            className="w-5 h-5 mt-0.5 rounded cursor-pointer text-primary border-border focus:ring-primary"
          />
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="font-semibold">{typeName}</span>
              <span className="inline-flex items-center rounded-full border border-border bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                <T kind="{{count}} license term" count={licenses.length} />
              </span>
            </div>
            <div className="mb-2">
              <div className="flex items-center text-sm mb-1">
                {availability.isOverActivated ? (
                  <span className="font-medium text-destructive">
                    <T
                      kind="None available (Over-activated by {{count}})"
                      count={availability.overActivated}
                    />
                  </span>
                ) : availability.available === 0 ? (
                  <span className="font-medium text-destructive">
                    <T kind="None available" />
                  </span>
                ) : (
                  <span className="font-medium">
                    <T
                      kind="{{available}} of {{total}} available"
                      available={availability.available}
                      total={availability.capacity}
                    />
                  </span>
                )}
              </div>
              <AvailabilityBar availability={availability} size="md" />
            </div>
            <button
              onClick={() => onToggleExpanded(typeName)}
              className="flex items-center gap-1 text-primary hover:opacity-80 text-sm font-medium"
              data-intercom-target="group-toggle-license-periods-button"
            >
              <svg
                className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
              {expanded ? (
                <T kind="Hide {{count}} license term" count={licenses.length} />
              ) : (
                <T kind="Show {{count}} license term" count={licenses.length} />
              )}
            </button>
          </div>
        </div>
      </div>
      {expanded && (
        <div className="p-3 space-y-2 border-t border-border bg-muted/10">
          {licenses.map((license) => (
            <LicenseTermRow
              key={license.id}
              license={license}
              selected={selectedLicenseIds.includes(license.id)}
              onToggle={handleToggleLicense}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default LicenseTypeCard;
